import { supabase } from '@/services/supabase';

const ADMIN_SESSION_STORAGE_KEY = 'impersonation.admin-session';
const IMPERSONATION_ACCESS_TOKEN_KEY = 'impersonation.access-token';
const LAST_STATE_STORAGE_KEY = 'impersonation.last-state';

type Listener = () => void;
const listeners = new Set<Listener>();
let cachedLastState: LastImpersonationState | null = null;

export function subscribeImpersonation(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners() {
  cachedLastState = readStorage<LastImpersonationState>(LAST_STATE_STORAGE_KEY);
  for (const listener of listeners) {
    listener();
  }
}

export function getLastImpersonationSnapshot() {
  if (cachedLastState === null) {
    cachedLastState = readStorage<LastImpersonationState>(LAST_STATE_STORAGE_KEY);
  }
  return cachedLastState;
}

type SessionSnapshot = {
  accessToken: string;
  refreshToken: string | null;
  adminName?: string | null;
  adminEmail?: string | null;
  adminPid?: string | null;
};

type JwtClaims = {
  email?: string;
  p_id?: string;
  impersonated_by?: string;
};

type LastImpersonationState = {
  impersonatedBy: string;
  targetEmail?: string;
};

function decodeBase64Url(value: string) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  return atob(padded);
}

export function decodeJwtClaims(token?: string | null): JwtClaims | null {
  if (!token) {
    return null;
  }

  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return null;
    }

    return JSON.parse(decodeBase64Url(payload)) as JwtClaims;
  } catch {
    return null;
  }
}

function writeStorage<T>(key: string, value: T | null) {
  if (value == null) {
    sessionStorage.removeItem(key);
    return;
  }

  sessionStorage.setItem(key, JSON.stringify(value));
}

function readStorage<T>(key: string): T | null {
  const raw = sessionStorage.getItem(key);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
}

export function storeAdminSession(snapshot: SessionSnapshot) {
  writeStorage(ADMIN_SESSION_STORAGE_KEY, snapshot);
}

export function getStoredAdminSession() {
  return readStorage<SessionSnapshot>(ADMIN_SESSION_STORAGE_KEY);
}

export function clearStoredAdminSession() {
  sessionStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
}

export function setImpersonationToken(accessToken: string) {
  sessionStorage.setItem(IMPERSONATION_ACCESS_TOKEN_KEY, accessToken);
  syncImpersonationState(accessToken);
}

export function getImpersonationToken() {
  return sessionStorage.getItem(IMPERSONATION_ACCESS_TOKEN_KEY);
}

export function clearImpersonationToken() {
  sessionStorage.removeItem(IMPERSONATION_ACCESS_TOKEN_KEY);
}

export function syncImpersonationState(accessToken?: string | null) {
  const claims = decodeJwtClaims(accessToken);
  if (claims?.impersonated_by) {
    writeStorage<LastImpersonationState>(LAST_STATE_STORAGE_KEY, {
      impersonatedBy: claims.impersonated_by,
      targetEmail: claims.email,
    });
    notifyListeners();
    return;
  }

  sessionStorage.removeItem(LAST_STATE_STORAGE_KEY);
  notifyListeners();
}

export function clearImpersonationState() {
  clearStoredAdminSession();
  clearImpersonationToken();
  sessionStorage.removeItem(LAST_STATE_STORAGE_KEY);
  notifyListeners();
}

export function isImpersonatingToken(accessToken?: string | null) {
  return !!decodeJwtClaims(accessToken)?.impersonated_by;
}

export function markLogoutRequested() {
  clearImpersonationState();
}

export function clearSkipRecovery() {}

export function shouldRecoverImpersonation() {
  return false;
}

export async function restoreAdminSession() {
  const adminSession = getStoredAdminSession();

  clearImpersonationState();

  if (!adminSession?.accessToken) {
    return false;
  }

  return true;
}

export async function getActiveAccessToken() {
  const impersonationToken = getImpersonationToken();
  if (impersonationToken) {
    return impersonationToken;
  }

  const session = await supabase.auth.getSession();
  return session.data.session?.access_token ?? null;
}
